import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Users, MapPin, TrendingUp, AlertTriangle, Shield,
  Award, RefreshCw, Activity, Brain, Target,
  ChevronDown, ChevronUp, Fingerprint, Filter, Search
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { CameroonLeafletMap } from './CameroonLeafletMap';
import {
  RadarChart as ReRadar, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, Cell, PieChart, Pie, ScatterChart, Scatter,
  ZAxis, ReferenceLine
} from 'recharts';
import CountUp from 'react-countup';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';

// ── Types ─────────────────────────────────────────────────────────────────────
interface Agent {
  employee_id:string; matricule:string; full_name:string;
  region:string; unit:string; rank:string; base_salary:number;
  present_verified:string|boolean; assignment_verified:string|boolean;
  training_hours:number; performance_score:number; attendance_rate:number;
  last_payroll_date:string; grade_index:number; date_recrutement:string;
  annees_service:number; jours_presents:number; jours_retard:number;
  jours_absents:number; score_formation:number; incidents_resolus:number;
  sanctions_disciplinaires:number; felicitations:number;
  derniere_promotion:string; annees_sans_promotion:number;
  score_attrition:number; risque_attrition:string;
  last_biometric_date:string; biometric_type:string; statut:string;
  gps_latitude:number; gps_longitude:number;
}

const API_URL=(import.meta as any).env?.VITE_API_URL||'https://nexus360-platform-backend-api-production.up.railway.app';
const GRADES=['Gardien de la Paix','Inspecteur de Police','Officier de Police','Commissaire de Police','Commissaire Divisionnaire'];
const REGIONS=['Adamaoua','Centre','Est','Extrême-Nord','Littoral','Nord','Nord-Ouest','Ouest','Sud','Sud-Ouest'];
const RISK_COLORS:Record<string,string>={CRITIQUE:'#ef4444',ELEVE:'#f97316',MODERE:'#eab308',FAIBLE:'#10b981'};
const RISK_BG:Record<string,string>={CRITIQUE:'bg-red-500/10 border-red-500/30 text-red-400',ELEVE:'bg-orange-500/10 border-orange-500/30 text-orange-400',MODERE:'bg-yellow-500/10 border-yellow-500/30 text-yellow-400',FAIBLE:'bg-green-500/10 border-green-500/30 text-green-400'};
const PAL=['#3b82f6','#10b981','#f97316','#a855f7','#eab308','#ef4444','#06b6d4','#ec4899'];
const TODAY=new Date().toISOString().slice(0,10);

const n=(v:any,fallback=0)=>{const x=Number(v);return isNaN(x)?fallback:x;};
const initials=(name:string)=>name.split(' ').map(w=>w[0]).join('').slice(0,2).toUpperCase();
const isPresent=(v:any)=>v===true||String(v).toUpperCase()==='TRUE'||v==='1';

// ── WOW: KPI Card ─────────────────────────────────────────────────────────────
const KPICard=({icon:Icon,label,numValue,suffix='',color,bg,border,decimals=0,alert=false}:
  {icon:any;label:string;numValue:number;suffix?:string;color:string;bg:string;border:string;decimals?:number;alert?:boolean})=>{
  const glowColor=color.includes('green')?'rgba(16,185,129,0.2)':color.includes('red')?'rgba(239,68,68,0.2)':color.includes('purple')?'rgba(168,85,247,0.2)':color.includes('cyan')?'rgba(6,182,212,0.2)':color.includes('yellow')?'rgba(234,179,8,0.2)':'rgba(59,130,246,0.2)';
  return(
    <motion.div whileHover={{scale:1.04,boxShadow:`0 0 18px ${glowColor}`}} transition={{duration:.15}}
      className={`rounded-xl p-3 border ${border} ${bg} flex items-center gap-3 cursor-default relative overflow-hidden`}
      style={{backdropFilter:'blur(8px)',background:'linear-gradient(135deg,rgba(15,23,42,0.85) 0%,rgba(30,41,59,0.6) 100%)'}}>
      <motion.div className="absolute top-0 left-0 right-0 h-px opacity-40"
        style={{background:`linear-gradient(90deg,transparent,${color.includes('green')?'#10b981':color.includes('red')?'#ef4444':color.includes('purple')?'#a855f7':color.includes('cyan')?'#06b6d4':color.includes('yellow')?'#eab308':'#3b82f6'},transparent)`}}
        animate={{x:['-100%','100%']}} transition={{duration:2.5,repeat:Infinity,ease:'linear',repeatDelay:1}}/>
      <Icon className={`w-5 h-5 ${color} flex-shrink-0 ${alert?'animate-pulse':''}`}/>
      <div>
        <p className={`font-bold text-lg leading-none ${color}`}>
          <CountUp end={numValue} duration={1.5} decimals={decimals} suffix={suffix} preserveValue separator=" "/>
        </p>
        <p className="text-slate-400 text-xs mt-0.5">{label}</p>
      </div>
      {alert&&<motion.div className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-red-400"
        animate={{opacity:[1,.2,1],scale:[1,1.5,1]}} transition={{duration:1,repeat:Infinity}}/>}
    </motion.div>
  );
};

// ── WOW: Skeleton ─────────────────────────────────────────────────────────────
const SkeletonCard=()=>(
  <div className="rounded-xl border border-blue-900/40 bg-slate-800/80 p-4">
    <Skeleton baseColor="#1e293b" highlightColor="#334155" height={14} width="55%" className="mb-3"/>
    <Skeleton baseColor="#1e293b" highlightColor="#334155" height={28} width="35%" className="mb-2"/>
    <Skeleton baseColor="#1e293b" highlightColor="#334155" height={10} count={2}/>
  </div>
);

// ── WOW: LIVE badge ───────────────────────────────────────────────────────────
const LiveBadge=({lastUpd,refreshing}:{lastUpd:Date;refreshing:boolean})=>(
  <div className="flex items-center gap-2">
    <motion.span className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold bg-green-500/20 text-green-300 border border-green-500/30"
      animate={{opacity:refreshing?[1,.5,1]:1}} transition={{duration:.6,repeat:refreshing?Infinity:0}}>
      <motion.span className="w-1.5 h-1.5 rounded-full bg-green-400"
        animate={{opacity:[1,.3,1],scale:[1,1.4,1]}} transition={{duration:1.5,repeat:Infinity}}/>
      LIVE
    </motion.span>
    <span className="text-slate-500 text-xs">{refreshing?'Actualisation…':lastUpd.toLocaleTimeString('fr-FR',{hour:'2-digit',minute:'2-digit'})}</span>
  </div>
);

// ── WOW: Glassmorphism Card ───────────────────────────────────────────────────
const Card=({icon:Icon,title,children,accent='text-blue-400',className=''}:
  {icon:any;title:string;children:React.ReactNode;accent?:string;className?:string})=>(
  <motion.div whileHover={{boxShadow:'0 0 24px rgba(59,130,246,0.07)'}}
    className={`rounded-xl border border-blue-900/40 overflow-hidden shadow-lg ${className}`}
    style={{background:'linear-gradient(135deg,rgba(15,23,42,0.9) 0%,rgba(30,41,59,0.65) 100%)',backdropFilter:'blur(8px)'}}>
    <div className="flex items-center gap-2 px-4 py-3 border-b border-blue-900/40"
      style={{background:'linear-gradient(90deg,rgba(30,41,59,0.85) 0%,rgba(15,23,42,0.5) 100%)'}}>
      <Icon className={`w-4 h-4 ${accent}`}/><span className="text-sm font-semibold text-white">{title}</span>
    </div>
    <div className="p-4">{children}</div>
  </motion.div>
);

const ProgBar=({value,color='bg-blue-500'}:{value:number;color?:string})=>(
  <div className="w-full bg-slate-700 rounded-full h-1.5 overflow-hidden">
    <motion.div className={`h-1.5 rounded-full ${color}`}
      initial={{width:0}} animate={{width:`${Math.min(100,value)}%`}} transition={{duration:1,ease:'easeOut'}}/>
  </div>
);

// ── Tooltip ───────────────────────────────────────────────────────────────────
const NexTip=({active,payload,label}:any)=>{
  if(!active||!payload?.length)return null;
  return(<motion.div initial={{opacity:0,scale:.95}} animate={{opacity:1,scale:1}} transition={{duration:.12}}
    className="bg-slate-900/95 border border-slate-600 rounded-xl p-3 shadow-2xl text-xs min-w-36 backdrop-blur">
    <p className="text-white font-semibold mb-2 pb-1 border-b border-slate-700">{label}</p>
    {payload.map((p:any,i:number)=>(<div key={i} className="flex justify-between gap-3 py-0.5"><div className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-sm" style={{background:p.fill||p.stroke||p.color}}/><span className="text-slate-300">{p.name}</span></div><span className="font-bold" style={{color:p.fill||p.stroke||p.color}}>{p.value}</span></div>))}
  </motion.div>);
};

// ── Radar 360 ─────────────────────────────────────────────────────────────────
const Radar360=({agent}:{agent:Agent})=>{
  const data=[
    {subject:'Présence', value:Math.min(100,n(agent.attendance_rate)),  fullMark:100},
    {subject:'Perf.',    value:Math.min(100,n(agent.performance_score)), fullMark:100},
    {subject:'Formation',value:Math.min(100,n(agent.score_formation)),   fullMark:100},
    {subject:'Incidents',value:Math.min(100,n(agent.incidents_resolus)/50*100), fullMark:100},
    {subject:'Mérite',   value:Math.min(100,n(agent.felicitations)/5*100),      fullMark:100},
    {subject:'Exp.',     value:Math.min(100,n(agent.annees_service)/25*100),     fullMark:100},
  ];
  return(<ResponsiveContainer width="100%" height={220}>
    <ReRadar data={data} margin={{top:8,right:24,bottom:8,left:24}}>
      <PolarGrid stroke="#1e3a5f"/>
      <PolarAngleAxis dataKey="subject" tick={{fill:'#94a3b8',fontSize:10}}/>
      <PolarRadiusAxis domain={[0,100]} tick={false} axisLine={false}/>
      <Radar name={agent.full_name} dataKey="value" stroke="#3b82f6" fill="#3b82f6" fillOpacity={.25} isAnimationActive animationDuration={1000}/>
      <Tooltip content={<NexTip/>}/>
    </ReRadar>
  </ResponsiveContainer>);
};

// ── Grade chart ───────────────────────────────────────────────────────────────
const GradeChart=({agents}:{agents:Agent[]})=>{
  const data=GRADES.map((g,i)=>({name:g.replace('Commissaire Divisionnaire','Comm. Div.').replace('Commissaire de Police','Commissaire').replace('Officier de Police','Officier').replace('Inspecteur de Police','Inspecteur').replace('Gardien de la Paix','Gardien'),count:agents.filter(a=>a.rank===g).length,fill:PAL[i]})).filter(d=>d.count>0);
  return(<ResponsiveContainer width="100%" height={420}>
    <BarChart data={data} margin={{top:8,right:8,left:0,bottom:8}} barCategoryGap="20%">
      <CartesianGrid strokeDasharray="3 3" stroke="#1e3a5f" vertical={false}/>
      <XAxis dataKey="name" tick={{fill:'#94a3b8',fontSize:9}} angle={-20} textAnchor="end" height={40} interval={0}/>
      <YAxis tick={{fill:'#94a3b8',fontSize:9}} allowDecimals={false} label={{value:'Effectif',angle:-90,position:'insideLeft',fill:'#64748b',fontSize:9,dx:12}}/>
      <Tooltip content={<NexTip/>}/>
      <Bar dataKey="count" name="Effectifs" radius={[3,3,0,0]} isAnimationActive animationDuration={1000} animationEasing="ease-out">
        {data.map((d,i)=><Cell key={i} fill={d.fill}/>)}
      </Bar>
    </BarChart>
  </ResponsiveContainer>);
};

// ── Biometric pie ─────────────────────────────────────────────────────────────
const BiometricPie=({agents}:{agents:Agent[]})=>{
  const map:Record<string,number>={};agents.forEach(a=>{map[a.biometric_type]=(map[a.biometric_type]||0)+1;});
  const data=Object.entries(map).map(([name,value],i)=>({name,value,fill:PAL[i]}));
  const todayCount=agents.filter(a=>a.last_biometric_date===TODAY).length;
  const pct=agents.length?Math.round(todayCount/agents.length*100):0;
  return(<div className="space-y-2">
    <div className="flex items-center justify-between mb-1">
      <span className="text-slate-400 text-xs">Aujourd'hui: <motion.span className="text-cyan-400 font-bold"
        animate={{opacity:[1,.6,1]}} transition={{duration:2,repeat:Infinity}}>
        <CountUp end={todayCount} duration={1.2} preserveValue/>
      </motion.span> pointages</span>
      <span className="text-cyan-400 font-bold text-sm">{pct}%</span>
    </div>
    <ResponsiveContainer width="100%" height={340}>
      <PieChart margin={{top:8,right:8,bottom:8,left:8}}>
        <Pie data={data} cx="50%" cy="50%" innerRadius={55} outerRadius={85} dataKey="value" paddingAngle={3}
          isAnimationActive animationDuration={1200} animationEasing="ease-out"
          label={({name,percent})=>`${name} ${(percent*100).toFixed(0)}%`} labelLine={{stroke:'#94a3b8',strokeWidth:.8}}>
          {data.map((d,i)=><Cell key={i} fill={d.fill}/>)}
        </Pie>
        <Tooltip content={<NexTip/>}/><Legend wrapperStyle={{fontSize:'10px',paddingTop:'4px'}}/>
      </PieChart>
    </ResponsiveContainer>
  </div>);
};

// ── Attrition scatter ─────────────────────────────────────────────────────────
const AttritionScatter=({agents}:{agents:Agent[]})=>{
  const data=agents.map(a=>({x:n(a.attendance_rate),y:n(a.score_attrition),z:n(a.annees_sans_promotion),name:a.full_name,risk:a.risque_attrition,fill:RISK_COLORS[a.risque_attrition]||'#10b981'}));
  const CustomDot=(props:any)=>{const{cx,cy,payload}=props;const r=Math.max(5,Math.min(14,5+(payload.z||0)*.8));return(<g><circle cx={cx} cy={cy} r={r+3} fill={payload.fill} fillOpacity={.12}/><circle cx={cx} cy={cy} r={r} fill={payload.fill} fillOpacity={.85} stroke="#0f172a" strokeWidth={1.5}/><circle cx={cx} cy={cy} r={r*.35} fill="white" fillOpacity={.4}/></g>);};
  const ScatterTip=({active,payload}:any)=>{if(!active||!payload?.length)return null;const d=payload[0]?.payload;return(<div className="bg-slate-900/95 border border-slate-600 rounded-xl p-3 shadow-2xl text-xs backdrop-blur" style={{minWidth:'160px'}}><p className="text-white font-semibold mb-1.5 pb-1 border-b border-slate-700">{d.name}</p><div className="space-y-0.5">{[['Risque',d.risk==='ELEVE'?'ÉLEVÉ':d.risk==='MODERE'?'MODÉRÉ':d.risk,d.fill],['Présence',d.x+'%',d.x>=80?'#10b981':d.x>=60?'#eab308':'#ef4444'],['Score attrition',d.y,d.fill],['Ans sans promo',d.z,'#f97316']].map(([k,v,c],i)=>(<div key={i} className="flex justify-between gap-3"><span className="text-slate-400">{k}</span><span className="font-bold" style={{color:String(c)}}>{v}</span></div>))}</div></div>);};
  return(<ResponsiveContainer width="100%" height={380}><ScatterChart margin={{top:16,right:16,bottom:16,left:16}}>
    <CartesianGrid strokeDasharray="4 4" stroke="#1e3a5f" strokeOpacity={.6}/>
    <XAxis type="number" dataKey="x" name="Présence %" domain={[0,100]} tick={{fill:'#94a3b8',fontSize:10}} label={{value:'Présence (%)',position:'insideBottom',offset:-8,fill:'#64748b',fontSize:10}}/>
    <YAxis type="number" dataKey="y" name="Score attrition" domain={[0,100]} tick={{fill:'#94a3b8',fontSize:10}} label={{value:'Score attrition',angle:-90,position:'insideLeft',fill:'#64748b',fontSize:10,dx:4}}/>
    <ZAxis type="number" dataKey="z" range={[30,160]}/>
    <Tooltip cursor={{stroke:'rgba(99,102,241,.3)',strokeWidth:1,strokeDasharray:'4 2'}} content={<ScatterTip/>}/>
    <ReferenceLine y={70} stroke="#ef4444" strokeDasharray="4 2" strokeOpacity={.7} label={{value:'Critique',position:'insideTopRight',fill:'#ef4444',fontSize:9}}/>
    <ReferenceLine y={50} stroke="#f97316" strokeDasharray="4 2" strokeOpacity={.7} label={{value:'Élevé',position:'insideTopRight',fill:'#f97316',fontSize:9}}/>
    <ReferenceLine x={70} stroke="#3b82f6" strokeDasharray="4 2" strokeOpacity={.5} label={{value:'Seuil présence',position:'insideTopLeft',fill:'#3b82f6',fontSize:9}}/>
    <Scatter data={data} shape={<CustomDot/>} isAnimationActive animationDuration={1400} animationEasing="ease-out"/>
  </ScatterChart></ResponsiveContainer>);
};

// ── MAIN PAGE ─────────────────────────────────────────────────────────────────
export const HRIntelligenceV2=({user}:{user:any})=>{
  const[agents,setAgents]=useState<Agent[]>([]);
  const[loading,setLoading]=useState(true);
  const[refreshing,setRefreshing]=useState(false);
  const[lastUpd,setLastUpd]=useState(new Date());
  const[selAgent,setSelAgent]=useState<Agent|null>(null);
  const[selRegion,setSelRegion]=useState<string|null>(null);
  const[riskFilter,setRiskF]=useState('ALL');
  const[sortDesc,setSortDesc]=useState(true);
  const[search,setSearch]=useState('');
  const[regionF,setRegionF]=useState('');
  const[rankF,setRankF]=useState('');
  const[statutF,setStatutF]=useState('');
  const[page,setPage]=useState(0);
  const PAGE=15;

  const fetchAgents=useCallback(async()=>{
    setRefreshing(true);
    try{
      const res=await fetch(`${API_URL}/api/hr/agents`);
      const json=await res.json();
      if(json.success&&Array.isArray(json.data)&&json.data.length>0){
        setAgents(json.data.map((a:any)=>({...a,base_salary:n(a.base_salary),training_hours:n(a.training_hours),performance_score:n(a.performance_score),attendance_rate:n(a.attendance_rate),grade_index:n(a.grade_index),annees_service:n(a.annees_service),jours_presents:n(a.jours_presents),jours_retard:n(a.jours_retard),jours_absents:n(a.jours_absents),score_formation:n(a.score_formation),incidents_resolus:n(a.incidents_resolus),sanctions_disciplinaires:n(a.sanctions_disciplinaires),felicitations:n(a.felicitations),annees_sans_promotion:n(a.annees_sans_promotion),score_attrition:n(a.score_attrition),gps_latitude:n(a.gps_latitude),gps_longitude:n(a.gps_longitude)})));
      }
    }catch(e){console.error('HR V2 fetch:',e);}
    finally{setLoading(false);setRefreshing(false);setLastUpd(new Date());}
  },[]);
  useEffect(()=>{fetchAgents();},[fetchAgents]);
  useEffect(()=>{const t=setInterval(fetchAgents,30000);return()=>clearInterval(t);},[fetchAgents]);

  const filtered=useMemo(()=>agents.filter(a=>(!regionF||a.region===regionF)&&(!rankF||a.rank===rankF)&&(!statutF||a.statut===statutF)&&(!search||[a.full_name,a.matricule,a.unit].join(' ').toLowerCase().includes(search.toLowerCase()))),[agents,regionF,rankF,statutF,search]);
  const display=selRegion?filtered.filter(a=>a.region===selRegion):filtered;
  const totalAgents=filtered.length,avgPresence=totalAgents?Math.round(filtered.reduce((s,a)=>s+n(a.attendance_rate),0)/totalAgents):0,avgPerf=totalAgents?Math.round(filtered.reduce((s,a)=>s+n(a.performance_score),0)/totalAgents):0,highRisk=filtered.filter(a=>a.risque_attrition==='CRITIQUE'||a.risque_attrition==='ELEVE').length,biometricToday=filtered.filter(a=>a.last_biometric_date===TODAY).length,activeCount=filtered.filter(a=>a.statut==='Actif').length;

  const regionStats=useMemo(()=>{const m:Record<string,{count:number;pres:number;perf:number;risk:number}>={};filtered.forEach(a=>{if(!m[a.region])m[a.region]={count:0,pres:0,perf:0,risk:0};m[a.region].count++;m[a.region].pres+=n(a.attendance_rate);m[a.region].perf+=n(a.performance_score);m[a.region].risk+=n(a.score_attrition);});return Object.entries(m).map(([region,d])=>({region,count:d.count,presence:Math.round(d.pres/d.count),perf:Math.round(d.perf/d.count),risk:Math.round(d.risk/d.count)})).sort((a,b)=>b.count-a.count);},[filtered]);
  const attritionList=useMemo(()=>[...display].filter(a=>riskFilter==='ALL'||a.risque_attrition===riskFilter).sort((a,b)=>sortDesc?b.score_attrition-a.score_attrition:a.score_attrition-b.score_attrition).slice(0,12),[display,riskFilter,sortDesc]);
  const tableData=useMemo(()=>[...display].sort((a,b)=>b.score_attrition-a.score_attrition).slice(page*PAGE,(page+1)*PAGE),[display,page]);
  const totalPages=Math.ceil(display.length/PAGE);
  const clearFilters=()=>{setRegionF('');setRankF('');setStatutF('');setSearch('');setSelRegion(null);setPage(0);};
  const fadeUp={initial:{opacity:0,y:20},animate:{opacity:1,y:0},transition:{duration:.5}};

  // ── Skeleton Loader ──
  if(loading) return(
    <div className="p-4 md:p-6 space-y-4">
      <div className="flex items-center gap-3 mb-4">
        <Skeleton baseColor="#1e293b" highlightColor="#334155" width={200} height={28}/>
        <Skeleton baseColor="#1e293b" highlightColor="#334155" width={60} height={22} borderRadius={20}/>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">{Array(6).fill(0).map((_,i)=><SkeletonCard key={i}/>)}</div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4"><SkeletonCard/><SkeletonCard/></div>
      <div className="text-center text-slate-500 text-sm flex items-center justify-center gap-2 pt-4">
        <RefreshCw className="w-4 h-4 animate-spin text-blue-400"/>Chargement des données RH…
      </div>
    </div>
  );

  return(
    <div className="p-4 md:p-6 space-y-4" style={{background:'linear-gradient(180deg,rgba(2,6,23,0.25) 0%,transparent 100%)'}}>

      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <motion.div animate={{rotate:[0,-8,8,-8,0]}} transition={{duration:2,repeat:Infinity,repeatDelay:4}}>
              <Brain className="w-6 h-6 text-blue-400"/>
            </motion.div>
            <h1 className="text-2xl font-bold text-white">Intelligence RH</h1>
          </div>
          <p className="text-slate-400 text-sm">Effectifs · Radar 360° · Attrition ML · {totalAgents} agents</p>
        </div>
        <div className="flex items-center gap-3">
          <LiveBadge lastUpd={lastUpd} refreshing={refreshing}/>
          <button onClick={fetchAgents} disabled={refreshing}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-500/20 hover:bg-blue-500/30 border border-blue-500/30 text-blue-300 text-xs transition-colors disabled:opacity-50">
            <RefreshCw className={`w-3.5 h-3.5 ${refreshing?'animate-spin':''}`}/>Actualiser
          </button>
        </div>
      </div>

      {/* Filters */}
      <motion.div {...fadeUp} className="rounded-xl border border-blue-900/40 p-3 flex flex-wrap gap-3 items-center"
        style={{background:'linear-gradient(135deg,rgba(15,23,42,0.8) 0%,rgba(30,41,59,0.5) 100%)',backdropFilter:'blur(8px)'}}>
        <Filter className="w-4 h-4 text-blue-400 flex-shrink-0"/>
        <span className="text-slate-400 text-xs font-medium">Filtres:</span>
        <div className="relative">
          <Search className="w-3 h-3 text-slate-400 absolute left-2 top-1/2 -translate-y-1/2"/>
          <input value={search} onChange={e=>{setSearch(e.target.value);setPage(0);}} placeholder="Nom / matricule…"
            className="bg-slate-700/60 border border-slate-600/40 rounded-lg text-xs text-white pl-6 pr-3 py-1 w-36 focus:outline-none focus:border-blue-500/60"/>
        </div>
        {[{value:regionF,set:setRegionF,opts:REGIONS,ph:'Région'},{value:rankF,set:setRankF,opts:GRADES,ph:'Grade'},{value:statutF,set:setStatutF,opts:['Actif','Conge','Suspension'],ph:'Statut'}].map((f,i)=>(
          <select key={i} value={f.value} onChange={e=>{f.set(e.target.value);setPage(0);}} className="bg-slate-700/60 border border-slate-600/40 rounded-lg text-xs text-slate-300 px-2 py-1 focus:outline-none focus:border-blue-500/60">
            <option value="">{f.ph}</option>{f.opts.map(o=><option key={o} value={o}>{o}</option>)}
          </select>
        ))}
        {(search||regionF||rankF||statutF||selRegion)&&<button onClick={clearFilters} className="text-xs text-slate-400 hover:text-white px-2 py-1 rounded-lg border border-slate-600/40">✕ Effacer</button>}
      </motion.div>

      {/* KPI Bar with CountUp */}
      <motion.div {...fadeUp} transition={{duration:.5,delay:.1}} className="grid grid-cols-2 md:grid-cols-6 gap-3">
        <KPICard icon={Users}        label="Agents"             numValue={totalAgents}    color="text-blue-400"   bg="bg-blue-500/10"   border="border-blue-500/30"/>
        <KPICard icon={Activity}     label="Actifs"             numValue={activeCount}    color="text-green-400"  bg="bg-green-500/10"  border="border-green-500/30"/>
        <KPICard icon={MapPin}       label="Présence moy."      numValue={avgPresence}    suffix="%" color="text-cyan-400"   bg="bg-cyan-500/10"   border="border-cyan-500/30"/>
        <KPICard icon={TrendingUp}   label="Perf. moyenne"      numValue={avgPerf}        color="text-purple-400" bg="bg-purple-500/10" border="border-purple-500/30"/>
        <KPICard icon={AlertTriangle} label="Risque attrition"  numValue={highRisk}       color="text-red-400"    bg="bg-red-500/10"    border="border-red-500/30" alert={highRisk>0}/>
        <KPICard icon={Fingerprint}  label="Biom. aujourd'hui"  numValue={biometricToday} color="text-yellow-400" bg="bg-yellow-500/10" border="border-yellow-500/30"/>
      </motion.div>

      {/* ROW 1: Map + Scatter */}
      <motion.div {...fadeUp} transition={{duration:.5,delay:.2}} className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card icon={MapPin} title="Carte des effectifs" accent="text-green-400" className="h-full">
          <CameroonLeafletMap agents={filtered} selected={selRegion} onSelect={r=>setSelRegion(r===selRegion?null:r)}/>
          {selRegion&&(
            <div className="mt-2 flex items-center justify-between">
              <span className="text-blue-300 text-xs font-medium">Filtre: {selRegion} · {display.length} agents</span>
              <button onClick={()=>setSelRegion(null)} className="text-slate-400 text-xs hover:text-white">✕</button>
            </div>
          )}
        </Card>
        <Card icon={AlertTriangle} title="Analyse d'attrition — Présence vs Score de risque" accent="text-red-400" className="h-full">
          <AttritionScatter agents={display}/>
          <p className="text-slate-500 text-xs mt-1">Taille du point = années sans promotion · Cliquez un agent dans le tableau pour son profil 360°</p>
        </Card>
      </motion.div>

      {/* ROW 2: Region stats + Biometric + Grade */}
      <motion.div {...fadeUp} transition={{duration:.5,delay:.25}} className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card icon={TrendingUp} title="Statistiques par région" accent="text-blue-400">
          <div className="space-y-2 overflow-y-auto" style={{maxHeight:'420px'}}>
            {regionStats.map((r,i)=>(
              <motion.div key={i} initial={{opacity:0,x:-8}} animate={{opacity:1,x:0}} transition={{delay:i*.04}}
                whileHover={{scale:1.01}}
                onClick={()=>setSelRegion(r.region===selRegion?null:r.region)}
                className={`rounded-lg p-2.5 cursor-pointer transition-all border ${r.region===selRegion?'bg-blue-500/20 border-blue-500/40':'bg-slate-700/40 border-transparent hover:bg-slate-700/60'}`}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-white text-xs font-medium">{r.region}</span>
                  <span className="text-blue-300 text-xs font-bold">{r.count} agents</span>
                </div>
                <div className="grid grid-cols-3 gap-1 text-xs mb-1.5">
                  <div><p className="text-slate-400" style={{fontSize:'9px'}}>Présence</p><p className={`font-bold text-xs ${r.presence>=85?'text-green-400':r.presence>=70?'text-blue-400':r.presence>=55?'text-orange-400':'text-red-400'}`}>{r.presence}%</p></div>
                  <div><p className="text-slate-400" style={{fontSize:'9px'}}>Perf.</p><p className="text-purple-400 font-bold text-xs">{r.perf}</p></div>
                  <div><p className="text-slate-400" style={{fontSize:'9px'}}>Risque</p><p className={`font-bold text-xs ${r.risk>60?'text-red-400':r.risk>40?'text-orange-400':'text-green-400'}`}>{r.risk}</p></div>
                </div>
                <ProgBar value={r.presence} color={r.presence>=85?'bg-green-500':r.presence>=70?'bg-blue-500':r.presence>=55?'bg-orange-500':'bg-red-500'}/>
              </motion.div>
            ))}
          </div>
        </Card>
        <Card icon={Fingerprint} title="Authentification biométrique" accent="text-cyan-400">
          <BiometricPie agents={filtered}/>
        </Card>
        <Card icon={Award} title="Répartition par grade" accent="text-yellow-400">
          <GradeChart agents={filtered}/>
        </Card>
      </motion.div>

      {/* ROW 3: Attrition predictor + Radar 360 */}
      <motion.div {...fadeUp} transition={{duration:.5,delay:.3}} className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2">
          <Card icon={AlertTriangle} title="Prédicteur de risque d'attrition" accent="text-red-400" className="h-full">
            <div className="flex items-center gap-2 mb-3 flex-wrap">
              <span className="text-slate-400 text-xs">Filtrer par risque:</span>
              {['ALL','CRITIQUE','ELEVE','MODERE','FAIBLE'].map(r=>(
                <button key={r} onClick={()=>setRiskF(r)}
                  className={`px-2 py-0.5 rounded-full text-xs font-medium border transition-all ${riskFilter===r?(r==='ALL'?'bg-blue-500/20 text-blue-300 border-blue-500/40':RISK_BG[r]||''):'bg-slate-700/40 text-slate-400 border-slate-600/40 hover:bg-slate-700'}`}>
                  {r==='ALL'?'Tous':r==='ELEVE'?'ÉLEVÉ':r==='MODERE'?'MODÉRÉ':r}
                </button>
              ))}
              <button onClick={()=>setSortDesc(s=>!s)} className="ml-auto flex items-center gap-1 text-slate-400 text-xs hover:text-white">
                Score {sortDesc?<ChevronDown className="w-3 h-3"/>:<ChevronUp className="w-3 h-3"/>}
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {attritionList.map((a,i)=>{
                const rc=RISK_BG[a.risque_attrition]||RISK_BG.FAIBLE;
                const isSel=selAgent?.employee_id===a.employee_id;
                const riskColor=RISK_COLORS[a.risque_attrition]||'#10b981';
                return(
                  <motion.div key={a.employee_id} initial={{opacity:0,y:8}} animate={{opacity:1,y:0}} transition={{delay:i*.04}}
                    whileHover={{scale:1.01,boxShadow:a.risque_attrition==='CRITIQUE'?'0 0 10px rgba(239,68,68,0.15)':undefined}}
                    onClick={()=>setSelAgent(isSel?null:a)}
                    className={`flex items-center gap-3 p-2.5 rounded-lg border cursor-pointer transition-all ${isSel?'bg-blue-500/20 border-blue-500/40':`${rc} hover:opacity-90`}`}>
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                      {initials(a.full_name)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-xs font-medium truncate">{a.full_name}</p>
                      <p className="text-slate-400 text-xs truncate">{a.rank.replace('Commissaire de Police','Commissaire').replace('Inspecteur de Police','Inspecteur').replace('Officier de Police','Officier').replace('Gardien de la Paix','Gardien')}</p>
                      <div className="flex items-center gap-2 text-xs mt-0.5">
                        <span className={n(a.attendance_rate)>=80?'text-green-400':n(a.attendance_rate)>=60?'text-yellow-400':'text-red-400'}>{n(a.attendance_rate)}%</span>
                        <span className="text-slate-400">{a.annees_sans_promotion}a sans promo</span>
                      </div>
                    </div>
                    <div className="relative w-10 h-10 flex-shrink-0">
                      <svg viewBox="0 0 36 36" className="-rotate-90 w-10 h-10">
                        <circle cx="18" cy="18" r="14" fill="none" stroke="#1e293b" strokeWidth="3"/>
                        <motion.circle cx="18" cy="18" r="14" fill="none" stroke={riskColor} strokeWidth="3"
                          initial={{strokeDasharray:'0 88'}} animate={{strokeDasharray:`${n(a.score_attrition)*.88} 88`}}
                          transition={{duration:1.2,ease:'easeOut',delay:i*.05}} strokeLinecap="round"/>
                      </svg>
                      <span className="absolute inset-0 flex items-center justify-center text-white text-xs font-bold">{n(a.score_attrition)}</span>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </Card>
        </div>
        <Card icon={Target} title="Radar 360° — Profil agent" accent="text-blue-400" className="h-full">
          {selAgent?(
            <div className="space-y-3">
              <div className="flex items-center gap-3 p-2.5 rounded-lg bg-slate-700/60 border border-slate-600/40">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white text-sm font-bold flex-shrink-0">{initials(selAgent.full_name)}</div>
                <div className="flex-1 min-w-0">
                  <p className="text-white font-semibold text-sm truncate">{selAgent.full_name}</p>
                  <p className="text-slate-400 text-xs">{selAgent.rank}</p>
                  <p className="text-slate-400 text-xs">{selAgent.region} · <motion.span className={selAgent.statut==='Actif'?'text-green-400':selAgent.statut==='Conge'?'text-yellow-400':'text-red-400'}
                    animate={selAgent.statut!=='Actif'?{opacity:[1,.6,1]}:{}} transition={{duration:1.5,repeat:Infinity}}>
                    {selAgent.statut}
                  </motion.span></p>
                </div>
                <button onClick={()=>setSelAgent(null)} className="text-slate-400 hover:text-white text-xs">✕</button>
              </div>
              <Radar360 agent={selAgent}/>
              <div className="grid grid-cols-2 gap-1 text-xs">
                {[['Présence',`${n(selAgent.attendance_rate)}%`],['Performance',`${n(selAgent.performance_score)}`],['Formation',`${n(selAgent.score_formation)}%`],['Incidents',`${n(selAgent.incidents_resolus)}`],['Score attr.',`${n(selAgent.score_attrition)}`],['Risque',selAgent.risque_attrition]].map(([k,v],i)=>(
                  <div key={i} className="flex justify-between"><span className="text-slate-400">{k}</span><span className="text-blue-300 font-bold">{v}</span></div>
                ))}
              </div>
            </div>
          ):(
            <div className="flex flex-col items-center justify-center h-48 text-center">
              <motion.div animate={{opacity:[.4,1,.4]}} transition={{duration:2,repeat:Infinity}}>
                <Target className="w-8 h-8 text-slate-600 mb-2"/>
              </motion.div>
              <p className="text-slate-500 text-xs">Cliquez sur un agent dans le tableau pour afficher son profil Radar 360°</p>
            </div>
          )}
        </Card>
      </motion.div>

      {/* Agent Table */}
      <motion.div {...fadeUp} transition={{duration:.5,delay:.4}}>
        <motion.div whileHover={{boxShadow:'0 0 24px rgba(59,130,246,0.07)'}}
          className="rounded-xl border border-blue-900/40 overflow-hidden shadow-lg"
          style={{background:'linear-gradient(135deg,rgba(15,23,42,0.9) 0%,rgba(30,41,59,0.65) 100%)',backdropFilter:'blur(8px)'}}>
          <div className="flex items-center gap-2 px-4 py-3 border-b border-blue-900/40"
            style={{background:'linear-gradient(90deg,rgba(30,41,59,0.85) 0%,rgba(15,23,42,0.5) 100%)'}}>
            <Users className="w-4 h-4 text-blue-400 flex-shrink-0"/>
            <span className="text-sm font-semibold text-white">Registre des Agents</span>
            <span className="ml-auto text-xs text-slate-400">{display.length} agents</span>
          </div>
          <div className="overflow-x-auto">
            <div className="overflow-y-auto" style={{maxHeight:'400px'}}>
              <table className="w-full text-xs min-w-max">
                <thead className="sticky top-0 z-10">
                  <tr className="border-b border-blue-900/60 bg-slate-700">
                    {['Matricule','Nom','Grade','Région','Présence','Performance','Score attrition','Risque','Statut','Biométrie'].map(h=>(
                      <th key={h} className="px-3 py-2.5 text-left text-blue-300 font-semibold whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <AnimatePresence>
                    {tableData.map((a,i)=>{
                      const rb=RISK_BG[a.risque_attrition]||'';
                      return(
                        <motion.tr key={a.employee_id} initial={{opacity:0}} animate={{opacity:1}} transition={{delay:i*.02}}
                          whileHover={{backgroundColor:'rgba(59,130,246,0.05)'}}
                          onClick={()=>setSelAgent(selAgent?.employee_id===a.employee_id?null:a)}
                          className={`border-b border-blue-900/30 transition-colors cursor-pointer ${i%2===0?'bg-slate-800/40':'bg-slate-700/30'}`}>
                          <td className="px-3 py-2 text-slate-400">{a.matricule}</td>
                          <td className="px-3 py-2 text-white font-medium">{a.full_name}</td>
                          <td className="px-3 py-2 text-slate-300 whitespace-nowrap">{a.rank.replace('Commissaire de Police','Commissaire').replace('Inspecteur de Police','Inspecteur').replace('Officier de Police','Officier').replace('Gardien de la Paix','Gardien').replace('Commissaire Divisionnaire','Comm. Div.')}</td>
                          <td className="px-3 py-2 text-slate-300">{a.region}</td>
                          <td className="px-3 py-2">
                            <div className="flex items-center gap-1.5 min-w-20">
                              <ProgBar value={n(a.attendance_rate)} color={n(a.attendance_rate)>=80?'bg-green-500':n(a.attendance_rate)>=60?'bg-yellow-500':'bg-red-500'}/>
                              <span className={n(a.attendance_rate)>=80?'text-green-400':n(a.attendance_rate)>=60?'text-yellow-400':'text-red-400'}>{n(a.attendance_rate)}%</span>
                            </div>
                          </td>
                          <td className="px-3 py-2 text-purple-400 text-center">{n(a.performance_score)}</td>
                          <td className="px-3 py-2 text-center">
                            <motion.span className={n(a.score_attrition)>70?'text-red-400 font-bold':n(a.score_attrition)>50?'text-orange-400':n(a.score_attrition)>30?'text-yellow-400':'text-green-400'}
                              animate={n(a.score_attrition)>70?{opacity:[1,.6,1]}:{}} transition={{duration:1.5,repeat:Infinity}}>
                              {n(a.score_attrition)}
                            </motion.span>
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap">
                            <motion.span className={`px-2 py-0.5 rounded-full text-xs font-bold border ${rb}`}
                              animate={a.risque_attrition==='CRITIQUE'?{opacity:[1,.7,1]}:{}} transition={{duration:1.5,repeat:Infinity}}>
                              {a.risque_attrition==='ELEVE'?'ÉLEVÉ':a.risque_attrition==='MODERE'?'MODÉRÉ':a.risque_attrition}
                            </motion.span>
                          </td>
                          <td className="px-3 py-2 text-center">
                            <span className={a.statut==='Actif'?'text-green-400':a.statut==='Conge'?'text-yellow-400':'text-red-400'}>{a.statut}</span>
                          </td>
                          <td className="px-3 py-2 text-slate-400 text-center">{a.biometric_type}</td>
                        </motion.tr>
                      );
                    })}
                  </AnimatePresence>
                  {tableData.length===0&&<tr><td colSpan={10} className="px-3 py-8 text-center text-slate-500">Aucun agent trouvé</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
          <div className="px-4 py-2.5 border-t border-blue-900/40 flex items-center justify-between text-xs text-slate-400">
            <span>{display.length} agent(s) · Page {page+1}/{Math.max(1,totalPages)}</span>
            <div className="flex gap-2">
              <button onClick={()=>setPage(p=>Math.max(0,p-1))} disabled={page===0} className="px-2 py-1 rounded border border-slate-600/40 hover:bg-slate-700 disabled:opacity-30">&larr;</button>
              <button onClick={()=>setPage(p=>Math.min(totalPages-1,p+1))} disabled={page>=totalPages-1} className="px-2 py-1 rounded border border-slate-600/40 hover:bg-slate-700 disabled:opacity-30">&rarr;</button>
            </div>
          </div>
        </motion.div>
      </motion.div>

      {/* Footer */}
      <div className="rounded-xl border border-blue-900/30 bg-slate-800/40 px-4 py-3 flex flex-wrap items-center justify-between gap-2">
        <p className="text-slate-400 text-xs italic">Intelligence RH · Biométrie DGSN · Actualisation 30s</p>
        <div className="flex items-center gap-2"><Shield className="w-4 h-4 text-blue-400"/><span className="text-blue-300 text-xs font-medium">Données souveraines · DGSN Cameroun</span></div>
      </div>
    </div>
  );
};
